import React from 'react';
import { ParametroDefinicaoDTO } from '../integracaoInterfaces';

interface DadosEspecificosProps {
    parametrosDefinicao: ParametroDefinicaoDTO[];
    valoresEspecificos: {[key: string]: string};
    setValoresEspecificos: (v: {[key: string]: string}) => void;
    camposObrigatorios?: string[];
}

const DadosEspecificosCard: React.FC<DadosEspecificosProps> = ({ 
    parametrosDefinicao, valoresEspecificos, setValoresEspecificos, camposObrigatorios = []
}) => {
    const campos = parametrosDefinicao.filter(p => p.tipoParametro === 'CAMPO');
    const docSignOAuthHomologacao = 'https://account-d.docusign.com';
    const docSignOAuthProducao = 'https://account.docusign.com';
    const labels: {[key: string]: string} = {
        INTEGRATION_KEY: 'Integration Key (Client ID)',
        USER_ID: 'ID de usuário',
        ACCOUNT_ID: 'Account ID',
        ACCOUNT_BASE_URI: 'URI base da conta',
        OAUTH_BASE_URL: 'Ambiente DocSign',
        PRIVATE_KEY: 'Chave privada RSA',
        POLLING_FIXED_DELAY_MS: 'Intervalo de consulta de status (ms)'
    };

    const placeholders: {[key: string]: string} = {
        INTEGRATION_KEY: 'Client ID do aplicativo DocuSign, não o ID de usuário',
        USER_ID: 'GUID do usuário em Admin > Users ou sub do /oauth/userinfo',
        ACCOUNT_ID: 'Informe o ID da conta API',
        ACCOUNT_BASE_URI: 'Ex: https://na4.docusign.net',
        PRIVATE_KEY: 'Cole a chave privada PEM completa',
        POLLING_FIXED_DELAY_MS: 'Mínimo recomendado pela DocuSign: 900000 (15 minutos)'
    };

    const nomeCampo = (nomeParametro: string) => labels[nomeParametro] || nomeParametro.replace(/_/g, ' ');
    const campoObrigatorio = (nomeParametro: string) => camposObrigatorios.includes(nomeParametro);
    const isDocSign = campos.some(p => p.nomeParametro === 'INTEGRATION_KEY')
        && campos.some(p => p.nomeParametro === 'OAUTH_BASE_URL');
    const normalizarOAuthDocSign = (valor: string | undefined) => {
        const normalizado = String(valor || '').trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');
        return normalizado === 'account.docusign.com' ? docSignOAuthProducao : docSignOAuthHomologacao;
    };
    const montarRedirectUriDocSign = () => {
        const backendUrl = String(process.env.REACT_APP_ESTEIRA_BACK || '').trim().replace(/\/+$/, '');
        const baseUrl = backendUrl || `${window.location.protocol}//${window.location.hostname}:8080`;
        return `${baseUrl}/callback`;
    };
    const montarUrlConsentimentoDocSign = () => {
        const integrationKey = String(valoresEspecificos.INTEGRATION_KEY || '').trim();
        if (!integrationKey) return '';
        const baseOAuth = normalizarOAuthDocSign(valoresEspecificos.OAUTH_BASE_URL);
        const redirectUri = montarRedirectUriDocSign();
        return `${baseOAuth}/oauth/auth?response_type=code&scope=signature%20impersonation&client_id=${encodeURIComponent(integrationKey)}&redirect_uri=${encodeURIComponent(redirectUri)}`;
    };
    const abrirConsentimentoDocSign = () => {
        const urlConsentimento = montarUrlConsentimentoDocSign();
        if (!urlConsentimento) {
            window.alert('Preencha Integration Key (Client ID).');
            return;
        }
        window.open(urlConsentimento, '_blank', 'noopener,noreferrer');
    };

    if (campos.length === 0) return (
        <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light fw-bold">Dados Específicos</div>
            <div className="card-body">
                <div className="text-muted p-3">Nenhum dado específico necessário. Configure as credenciais na origem.</div>
            </div>
        </div>
    );

    return (
        <div className="card mb-4 shadow-sm">
            <div className="card-header bg-light fw-bold">Dados Específicos</div>
            <div className="card-body">
                <div className="row">
                    {campos.map(p => (
                        <div className={p.nomeParametro === 'PRIVATE_KEY' ? 'col-md-12 mb-3' : 'col-md-6 mb-3'} key={p.id}>
                            <label className="form-label">
                                {nomeCampo(p.nomeParametro)}{campoObrigatorio(p.nomeParametro) && <span className="text-danger"> *</span>}:
                            </label>
                            {p.nomeParametro === 'PRIVATE_KEY' ? (
                                <textarea
                                    className="form-control"
                                    rows={7}
                                    required={campoObrigatorio(p.nomeParametro)}
                                    placeholder={placeholders[p.nomeParametro] || `Informe ${nomeCampo(p.nomeParametro)}`}
                                    value={valoresEspecificos[p.nomeParametro] || ''}
                                    onChange={e => setValoresEspecificos({...valoresEspecificos, [p.nomeParametro]: e.target.value})}
                                />
                            ) : isDocSign && p.nomeParametro === 'OAUTH_BASE_URL' ? (
                                <select
                                    className="form-select"
                                    required={campoObrigatorio(p.nomeParametro)}
                                    value={normalizarOAuthDocSign(valoresEspecificos[p.nomeParametro])}
                                    onChange={e => setValoresEspecificos({...valoresEspecificos, [p.nomeParametro]: e.target.value})}
                                >
                                    <option value={docSignOAuthHomologacao}>Homologação</option>
                                    <option value={docSignOAuthProducao}>Produção</option>
                                </select>
                            ) : (
                                <input
                                    type={p.nomeParametro === 'POLLING_FIXED_DELAY_MS' ? 'number' : 'text'}
                                    min={p.nomeParametro === 'POLLING_FIXED_DELAY_MS' ? 900000 : undefined}
                                    step={p.nomeParametro === 'POLLING_FIXED_DELAY_MS' ? 60000 : undefined}
                                    className="form-control"
                                    required={campoObrigatorio(p.nomeParametro)}
                                    placeholder={placeholders[p.nomeParametro] || `Informe ${nomeCampo(p.nomeParametro)}`}
                                    value={valoresEspecificos[p.nomeParametro] || ''}
                                    onChange={e => setValoresEspecificos({...valoresEspecificos, [p.nomeParametro]: e.target.value})}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {isDocSign && (
                    <div className="d-flex justify-content-end">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            title={`redirect_uri=${montarRedirectUriDocSign()}`}
                            onClick={abrirConsentimentoDocSign}
                        >
                            Realizar permissão de usuário
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DadosEspecificosCard;
